/**
 * Utility for handling errors in the application
 */

/**
 * Format database error messages for user-friendly display
 * @param {Error} error - The error object
 * @returns {string} Formatted error message
 */
export const formatDatabaseError = (error) => {
  // If it's already a string, just return it
  if (typeof error === 'string') {
    return error;
  }
  
  // Get the error message
  const message = error.message || 'An unknown error occurred';
  
  // Check for specific SQLite error codes/messages and provide friendly messages
  if (message.includes('no such table')) {
    return 'The table you are trying to access does not exist';
  }
  
  if (message.includes('UNIQUE constraint failed')) {
    const column = message.match(/UNIQUE constraint failed: (\S+)/)?.[1];
    return `Duplicate value error: ${column ? `The value in column "${column.split('.')[1]}" already exists` : 'A record with this value already exists'}`;
  }
  
  if (message.includes('NOT NULL constraint failed')) {
    const column = message.match(/NOT NULL constraint failed: (\S+)/)?.[1];
    return `Required field error: ${column ? `The field "${column.split('.')[1]}" cannot be empty` : 'A required field is missing'}`;
  }
  
  if (message.includes('FOREIGN KEY constraint failed')) {
    return 'Referenced record not found: The record you are referencing does not exist';
  }
  
  if (message.includes('syntax error')) {
    return 'SQL syntax error: Check your query syntax';
  }
  
  // Authentication errors
  if (message.includes('authentication')) {
    return 'Authentication failed: Your credentials may have expired';
  }
  
  // Connection errors
  if (message.includes('connection')) {
    return 'Database connection error: Please check your internet connection';
  }
  
  // Return a generic but informative message if no specific case matched
  return `Database error: ${message}`;
};

/**
 * Log error details to console in development environment
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 */
export const logError = (error, context = '') => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(`Error ${context ? `in ${context}` : ''}:`, error);
  }
};

/**
 * Combined function to handle database errors - logs and formats the error
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 * @returns {string} Formatted error message
 */
export const handleDatabaseError = (error, context = '') => {
  logError(error, context);
  return formatDatabaseError(error);
};

/**
 * Format API error messages for user-friendly display
 * @param {Error} error - The error object 
 * @returns {string} Formatted error message
 */
export const formatApiError = (error) => {
  // If it's already a string, just return it
  if (typeof error === 'string') {
    return error;
  }
  
  // Check for Fetch API Response object
  if (error.status) {
    switch (error.status) {
      case 401:
        return 'Authentication failed: Please log in again';
      case 403:
        return 'Access denied: You do not have permission to perform this action';
      case 404:
        return 'Resource not found: The requested data does not exist';
      case 500:
        return 'Server error: The server encountered an error processing your request';
      default:
        return `API error (${error.status}): ${error.statusText || 'Unknown error'}`;
    }
  }
  
  // Default error message
  return `Error: ${error.message || 'An unknown error occurred'}`;
};

/**
 * Combined function to handle API errors - logs and formats the error
 * @param {Error} error - The error object
 * @param {string} context - Context where the error occurred
 * @returns {string} Formatted error message
 */
export const handleApiError = (error, context = '') => {
  logError(error, context);
  return formatApiError(error);
};